<template>
	<v-modal class="resource-timeline">
		<template v-slot:header>
			<h1>Arrange Appointment</h1>
		</template>
		<loader v-if="!calendarReady"/>
		<template v-if="calendarReady">
			<div v-if="!calendarData.appointees.length" class="nothing-to-see-here">
				<p>No appointees available.</p>
			</div>
			<template v-if="calendarData.appointees.length">
				<header>
					<select v-model="appointee" class="resource-timeline__calendar__resource__selector">
						<option value="">Select an appointee</option>
						<option
							v-for="(appointee, appointeeIndex) in calendarData.appointees" :value="appointee.id"
							v-html="$present('fullName', appointee.profile)"></option>
					</select>
				</header>
				<div v-if="appointee" class="resource-timeline__calendar">
					<v-date-picker
						v-model="date"
						class="resource-timeline__calendar__datepicker"
						is-inline
						title-position="left"
						@update:to-page="(page) => select1stOf(page)"/>
					<!--:min-date="new Date()"-->
					<section class="resource-timeline__calendar__resource">
						<full-calendar
							ref="calendar"
							:options="calendarOptions"/>
					</section>
				</div>
			</template>
		</template>
		<template v-if="calendarReady" v-slot:footer class="resource-timeline__confirmation">
			<p v-if="newEvent" v-html="confirmMessage"/>
			<ul class="v-modal__footer__actions">
				<li>
					<button class="button button--secondary" @click="cancel">Cancel</button>
				</li>
				<li v-if="newEvent">
					<process-button
						:processing="saving" class="button button--primary" text="Save Appointment" @click="save"/>
				</li>
			</ul>
		</template>
	</v-modal>
</template>
<script>
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import FullCalendar from "@fullcalendar/vue";
import {createNamespacedHelpers} from "vuex";

const {mapActions} = createNamespacedHelpers("leads");

export default {
	components: {
		FullCalendar,
	},

	data() {
		return {
			appointee: "",
			calendarData: null,
			calendarReady: false,
			date: "",
			newEvent: null,
			saving: false,
		};
	},

	props: {
		client: {
			required: true,
			type: Object,
		},
		lead: {
			required: true,
			type: Object,
		},
	},

	watch: {
		date(newDate) {
			this.$refs.calendar.getApi().gotoDate(this.date);
		},
		appointee() {
			this.clearNewEvent();
		},
	},

	computed: {
		calendarOptions() {
			let self = this;
			return {
				plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
				// timeZone: 'Europe/London',
				height: "100%",
				headerToolbar: false,
				views: {
					timeGrid: {
						titleFormat: {year: "numeric", day: "2-digit"},
					},
					week: {
						titleFormat: {year: "numeric", day: "2-digit"},
					},
				},
				initialView: "timeGridWeek",
				firstDay: 1,
				allDaySlot: false,
				nowIndicator: true,
				businessHours: this.businessHours,
				slotMinTime: this.slotMinTime,
				slotMaxTime: this.slotMaxTime,
				selectable: true,
				unselectAuto: true,
				selectOverlap: false,
				selectConstraint: "businessHours",
				defaultAllDay: false,
				defaultTimedEventDuration: this.defaultTimedEventDuration,
				forceEventDuration: true,
				editable: true,
				select: function (info) {
					console.log(info);

					self.clearNewEvent();

					let start = moment().utcOffset(info.start, false);
					let end = moment(info.start).add(self.calendarData.settings.default_duration, "minutes");
					let overlappedEvent = self.isAnOverlapEvent(start, end);

					if (overlappedEvent) {
						return;
					}

					self.$refs.calendar.getApi().addEvent({
						start: info.start,
						end: end,
						display: "background",
						color: "#257e4a",
					});
				},
				eventAdd: function (info) {
					console.log("eventAdd");
					self.newEvent = info.event;
				},
				events: self.events,
			};
		},
		selectedAppointee() {
			let self = this;
			return _.find(self.calendarData.appointees, function (appointee) {
				return appointee.id === self.appointee;
			});
		},
		businessHours() {
			return this.selectedAppointee.availability;
		},
		slotMinTime() {
			if (!this.selectedAppointee.availability.length) {
				return moment("09:00", "HH:mm").subtract(1, "hours").format("HH:mm");
			}

			let earliest = _.minBy(this.selectedAppointee.availability, function (data) {
				return data.startTime;
			}).startTime;

			return moment(earliest, "HH:mm").subtract(1, "hours").format("HH:mm");
		},
		slotMaxTime() {
			if (!this.selectedAppointee.availability.length) {
				return moment("17:00", "HH:mm").add(1, "hours").format("HH:mm");
			}

			let latest = _.maxBy(this.selectedAppointee.availability, function (data) {
				return data.endTime;
			}).endTime;

			return moment(latest, "HH:mm").add(1, "hours").format("HH:mm");
		},
		defaultTimedEventDuration() {
			let duration = moment.duration(this.calendarData.settings.default_duration, "minutes");
			return moment.utc(duration.asMilliseconds()).format("HH:mm");
		},
		events() {
			return _.map(this.selectedAppointee.calendarEvents, function (event) {
				return {
					display: "background",
					color: "#ff9f89",
					allDay: event.all_day,
					start: event.start_at,
					end: event.end_at,
				};
			});
		},
		confirmMessage() {
			return "Confirm appointment for " + this.$present("date", this.newEvent.start) + " with " + this.$present("fullName", this.selectedAppointee.profile);
		},
	},

	methods: {
		...mapActions([
			"updateModel",
		]),
		select1stOf(page) {
			console.log("select1stOf", page);
			let date = new Date(page.year, page.month - 1, 1);
			this.date = date;
		},
		isAnOverlapEvent(start, end) {
			let newEventRange = moment.range(start, end);
			let events = this.$refs.calendar.getApi().getEvents();

			let overlappedEvent = _.findIndex(events, function (event) {
				let existingEventRange = moment.range(moment(event.start), moment(event.end));
				return event.id && newEventRange.overlaps(existingEventRange);
			});

			return overlappedEvent > -1;
		},
		clearNewEvent() {
			if (!this.newEvent) {
				return;
			}

			this.newEvent.remove();
			this.newEvent = null;
		},
		cancel() {
			this.$vmodal.close();
		},
		save() {
			this.saving = true;
			http().post("clients/" + this.lead.client.id + "/provisional-appointment", {
				appointee: this.selectedAppointee.id,
				start: moment(this.newEvent.start).format("YYYY/MM/DD HH:mm"),
				end: moment(this.newEvent.end).format("YYYY/MM/DD HH:mm"),
				service: this.lead.service.id,
			}).then(response => {
				// this.$store.dispatch('clients/activateModel', {id: this.lead.client.id}).then(() => {
				// 	this.saving = false
				// 	this.$vmodal.close()
				// 	this.$store.dispatch('alerts/success', 'Appointment Created')
				// })
				this.$store.commit("clients/PUSH_APPOINTMENT", {
					appointment: response.data.data,
					client: this.client,
					leadId: this.lead.id,
				});
				this.$vmodal.close();
				this.$store.dispatch("alertsStore/success", "Appointment Created");
			});
		},
	},

	created() {
		http().get("form-data/appointees", {
			params: {
				lead: this.lead.id,
			},
		}).then(response => {
			this.calendarData = response.data;
			// this.appointee = this.calendarData.appointees.length ? this.calendarData.appointees[0].id : null
			this.calendarReady = true;
		});
	},
};
</script>
